import useStore from "./useStore";
import { isGlobalProcessType, PROCESS_STATUS } from "@/store/subscriptionCore/processConstants";
import { IS_DEV } from "@/utils/env";

/**
 * Subscribes to the latest process row from ProcessTrackerStore for `type` (and optional `propertyId`).
 * That row is whichever run has the greatest `startedAt` among matches — including finished runs.
 * So `status === done` or `error` means the last run settled that way, not that the UI is idle or ready
 * for a new action unless you also interpret `phase` or `isRunning`.
 *
 * @param {string} type - Value from `PROCESS_TYPE` in processConstants
 * @param {string|null} [propertyId] - Omit for {@link isGlobalProcessType} flows; otherwise scopes to that property
 * @returns {{status: string, progressMessage: string, progressStage: string, error: string|null, result: Object|null, isRunning: boolean, phase: 'idle'|'running'|'settled', process: Object|null}}
 */
const useProcessStatus = (type, propertyId = null) => {
  const { processTrackerStore } = useStore();
  if (IS_DEV && propertyId == null && !isGlobalProcessType(type)) {
    console.warn(
      `useProcessStatus("${type}") called without propertyId; global scope only. Pass propertyId for property-scoped types.`
    );
  }
  const process =
    propertyId == null
      ? processTrackerStore?.getLatestGlobalProcess(type) ?? null
      : processTrackerStore?.getLatestPropertyProcess(type, propertyId) ?? null;

  // Do not wrap in useMemo([process]): ProcessTrackerStore mutates the entry object in place
  // (Object.assign on the same Map value). The reference is stable while status moves
  // invoking → processing → done, so memoization would freeze UI until a new run starts.
  const status = process?.status ?? PROCESS_STATUS.IDLE;
  const isRunning = [PROCESS_STATUS.INVOKING, PROCESS_STATUS.PROCESSING].includes(status);
  const isSettled = [PROCESS_STATUS.DONE, PROCESS_STATUS.ERROR].includes(status);
  const phase = isRunning ? "running" : isSettled ? "settled" : "idle";

  return {
    status,
    progressMessage: process?.progressMessage ?? "",
    progressStage: process?.progressStage ?? "",
    error: process?.error ?? null,
    result: process?.result ?? null,
    isRunning,
    phase,
    process,
  };
};

export default useProcessStatus;
